/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from "react";

import { Box, BoxProps, Grid } from "@material-ui/core";

import { MopButton } from "client/components";
import { FieldMaps, useMerchantModel } from "client/hooks";
import { FieldSet } from "client/lib/apt-merchant";
import { getEmail } from "client/utils";

import { FieldRenderer } from "./FieldRenderer";

type FormElements<T> = HTMLFormControlsCollection &
  {
    [P in keyof T]?: HTMLInputElement | HTMLSelectElement;
  };

export interface FormElement<T> extends HTMLFormElement {
  readonly elements: FormElements<T>;
}

interface RenderFormGroup {
  containerBoxProps?: BoxProps;
  currentConversationalPage?: number;
  customSubmitComponent?: React.ReactNode;
  customValidator?: (event: React.FormEvent<HTMLFormElement>) => boolean;
  footer?: React.ReactNode;
  fieldSet: FieldSet<any>;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submitLabel?: string;
  uiFieldMap?: FieldMaps;
}

export const FormBuilder: React.FC<RenderFormGroup> = ({
  containerBoxProps = {},
  footer,
  fieldSet,
  onSubmit,
  currentConversationalPage,
  customValidator,
  customSubmitComponent,
  submitLabel = "Next",
  uiFieldMap,
}) => {
  const { validateMerchant, merchant } = useMerchantModel();
  if (!fieldSet) {
    return null;
  }

  const formId = `${fieldSet.fieldSetName}`.replace(/ /gi, "");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const isValid = customValidator
      ? customValidator(e)
      : validateMerchant(fieldSet);
    if (isValid) {
      /**
        Marketing initiable - bizible data tracking:

        The following code snippet is required in order to get Bizible to function properly since we're not collecting the email address + sending to Bizible directly from an HTML form

        https://experienceleague.adobe.com/docs/marketo-measure/using/marketo-measure-tracking/setting-up-tracking/adding-marketo-measure-script-to-different-form-providers/ajax-form-handling.html?lang=en#scenario-lead-information-collected-in-a-non-html-form
       * */

      window["Bizible"]?.Push("User", {
        eMail: getEmail(merchant),
      });
      onSubmit(e);
    }
  };

  return (
    <Box {...containerBoxProps}>
      <form onSubmit={handleSubmit} id={formId}>
        <FieldRenderer
          currentConversationalPage={currentConversationalPage}
          fieldSet={fieldSet}
          uiFieldMap={uiFieldMap}
        />
        {footer}
        <Box mt={3}>
          <Grid xs={12} item>
            {customSubmitComponent ? (
              customSubmitComponent
            ) : (
              <MopButton mopVariant="primary" type="submit" fullWidth>
                {submitLabel}
              </MopButton>
            )}
          </Grid>
        </Box>
      </form>
    </Box>
  );
};
